import React, { useState, useEffect, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import { formatPhone } from "../../utils/formatPhone";

const EditCustomerForm = ({ customer, onCancel, onUpdated }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    status_id: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    city: "",
    state_id: "",
    postal_code: "",
    organization_type: "",
    // Tax and payment settings
    tax_exempt: false,
    default_service_tax_rate: 0,
    default_mileage_tax_rate: 0,
    payment_terms: 30,
  });

  const [states, setStates] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  // Check if invoice enhancements are enabled for this tenant
  const invoiceEnhancementsEnabled = user?.enable_invoices === true;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (customer) {
      const initialData = {
        name: customer.name || "",
        email: customer.email || "",
        status_id: customer.status_id || "",
        phone_number: formatPhone(customer.phone_number || ""),
        organization_type: customer.organization_type || "",
        address_line_1: customer.address?.address_line_1 || "",
        address_line_2: customer.address?.address_line_2 || "",
        city: customer.address?.city || "",
        state_id: customer.address?.state_id || "",
        postal_code: customer.address?.postal_code || "",
        // Tax and payment settings
        tax_exempt: customer.tax_exempt || false,
        default_service_tax_rate: customer.default_service_tax_rate || 0,
        default_mileage_tax_rate: customer.default_mileage_tax_rate || 0,
        payment_terms: customer.payment_terms || 30,
      };
      setFormData(initialData);
      setOriginalData(initialData); // Store for change detection
    }
  }, [customer]);

  const fetchInitialData = async () => {
    try {
      const [statesRes, statusesRes] = await Promise.all([
        secureAxios.get(`${apiBase}/api/states`),
        secureAxios.get(`${apiBase}/api/status`),
      ]);

      setStates(statesRes.data);
      setStatuses(statusesRes.data);
    } catch (err) {
      console.error("Error loading form data", err);
      setError("Failed to load form data. Please refresh the page.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue;

    if (type === "checkbox") {
      newValue = checked;
      // If tax_exempt is checked, reset tax rates to 0
      if (name === "tax_exempt" && checked) {
        setFormData((prev) => ({
          ...prev,
          tax_exempt: true,
          default_service_tax_rate: 0,
          default_mileage_tax_rate: 0,
        }));
        if (error) setError(null);
        return;
      }
    } else if (name === "phone_number") {
      newValue = formatPhone(value);
    } else if (
      name === "default_service_tax_rate" ||
      name === "default_mileage_tax_rate"
    ) {
      // Handle tax rate inputs - allow decimals
      const numValue = parseFloat(value) || 0;
      newValue = Math.max(0, Math.min(25, numValue)); // Clamp between 0-25%
    } else if (name === "payment_terms") {
      newValue = parseInt(value) || 30;
    } else {
      newValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (error) setError(null);
  };

  const getChangedFields = () => {
    const changes = [];
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== originalData[key]) {
        changes.push(key);
      }
    });
    return changes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.status_id || !formData.organization_type) {
        setError("Name, email, status, and organization type are required");
        setLoading(false);
        return;
      }

      const cleanedPhone = formData.phone_number.replace(/\D/g, "");

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        status_id: formData.status_id,
        phone_number: cleanedPhone,
        address_line_1: formData.address_line_1.trim(),
        address_line_2: formData.address_line_2.trim(),
        city: formData.city.trim(),
        state_id: formData.state_id,
        postal_code: formData.postal_code.trim(),
        organization_type: formData.organization_type.trim(),
        // Tax and payment settings
        tax_exempt: formData.tax_exempt,
        default_service_tax_rate: formData.tax_exempt
          ? 0
          : parseFloat(formData.default_service_tax_rate) || 0,
        default_mileage_tax_rate: formData.tax_exempt
          ? 0
          : parseFloat(formData.default_mileage_tax_rate) || 0,
        payment_terms: parseInt(formData.payment_terms) || 30,
      };

      await secureAxios.put(
        `${apiBase}/api/customers/${customer.customer_id}`,
        payload
      );

      // Log which fields were changed for audit purposes
      const changedFields = getChangedFields();
      console.log("Customer updated. Changed fields:", changedFields);

      alert("Customer updated successfully!");

      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(err.response?.data?.error || "Failed to update customer");
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  if (!customer) {
    return <div>No customer selected for editing.</div>;
  }

  return (
    <div>
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* PHI Access Warning */}
      <div className="alert alert-info mb-3" role="alert">
        <i className="bi bi-shield-lock"></i>{" "}
        <strong>Editing Protected Health Information</strong>
        <br />
        You are editing customer ID: <code>{customer.customer_id}</code>
        <br />
        All changes will be logged for HIPAA compliance.
        {customer.organization_type === "customer" && (
          <>
            <br />
            <i className="bi bi-info-circle"></i> <strong>Note:</strong> Changes
            to name, phone, and address will also update the corresponding
            tenant record.
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Name */}
        <div className="col-md-6">
          <label className="form-label">
            Name <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </div>

        {/* Email */}
        <div className="col-md-6">
          <label className="form-label">
            Email <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </div>

        {/* Status */}
        <div className="col-md-6">
          <label className="form-label">
            Status <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            name="status_id"
            value={formData.status_id}
            onChange={handleChange}
            disabled={loading}
            required
          >
            <option value="">Select Status</option>
            {statuses.map((s) => (
              <option key={s.status_id} value={s.status_id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>

        {/* Organization Type */}
        <div className="col-md-6">
          <label className="form-label">
            Organization Type <span className="text-danger">*</span>
          </label>
          <input
            className="form-control"
            name="organization_type"
            value={formData.organization_type}
            onChange={handleChange}
            disabled={loading}
            required
          />
        </div>

        {/* Phone Number */}
        <div className="col-md-6">
          <label className="form-label">Phone Number</label>
          <input
            className="form-control"
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            disabled={loading}
            placeholder="(123) 456-7890"
          />
        </div>

        {/* Address Section */}
        <div className="col-12">
          <h5 className="border-bottom pb-2">Address Information</h5>
        </div>

        {/* Address Line 1 */}
        <div className="col-12">
          <label className="form-label">Address Line 1</label>
          <input
            className="form-control"
            name="address_line_1"
            value={formData.address_line_1}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* Address Line 2 */}
        <div className="col-12">
          <label className="form-label">Address Line 2</label>
          <input
            className="form-control"
            name="address_line_2"
            value={formData.address_line_2}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* City */}
        <div className="col-md-4">
          <label className="form-label">City</label>
          <input
            className="form-control"
            name="city"
            value={formData.city}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {/* State Dropdown */}
        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            name="state_id"
            value={formData.state_id}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="">Select State</option>
            {states.map((state) => (
              <option key={state.state_id} value={state.state_id}>
                {state.state_code} - {state.state_name}
              </option>
            ))}
          </select>
        </div>

        {/* ZIP Code */}
        <div className="col-md-4">
          <label className="form-label">ZIP Code</label>
          <input
            className="form-control"
            name="postal_code"
            value={formData.postal_code}
            onChange={handleChange}
            disabled={loading}
            maxLength="10"
          />
        </div>

        {/* ============================================ */}
        {/* TAX & BILLING SETTINGS SECTION */}
        {/* ============================================ */}
        {/* Tax & Billing Settings - Only show if invoice enhancements enabled */}
        {invoiceEnhancementsEnabled && (
          <>
            <div className="col-12">
              <h5 className="border-bottom pb-2 mt-3">
                <i className="bi bi-receipt me-2"></i>
                Tax &amp; Billing Settings
              </h5>
              <p className="text-muted small">
                These settings are used when generating invoices for this
                customer.
              </p>
            </div>

            {/* Tax Exempt Checkbox */}
            <div className="col-12">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="tax_exempt"
                  name="tax_exempt"
                  checked={formData.tax_exempt}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="tax_exempt">
                  <strong>Tax Exempt</strong>
                </label>
                <div className="form-text">
                  If checked, no tax will be calculated for invoices to this
                  customer
                </div>
              </div>
            </div>

            {/* Tax Rates (shown when not tax exempt) */}
            {!formData.tax_exempt && (
              <>
                <div className="col-md-4">
                  <label className="form-label">
                    Default Service Tax Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="default_service_tax_rate"
                      value={formData.default_service_tax_rate}
                      onChange={handleChange}
                      min="0"
                      max="25"
                      step="0.001"
                      disabled={loading}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text">
                    Tax rate for job coaching services (e.g., 6.5 for 6.5%)
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Default Mileage Tax Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="default_mileage_tax_rate"
                      value={formData.default_mileage_tax_rate}
                      onChange={handleChange}
                      min="0"
                      max="25"
                      step="0.001"
                      disabled={loading}
                    />
                    <span className="input-group-text">%</span>
                  </div>
                  <div className="form-text">
                    Tax rate for mileage reimbursement (often 0%)
                  </div>
                </div>
              </>
            )}

            {/* Payment Terms */}
            <div className="col-md-4">
              <label className="form-label">Payment Terms</label>
              <select
                className="form-select"
                name="payment_terms"
                value={formData.payment_terms}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="15">Net 15</option>
                <option value="30">Net 30</option>
                <option value="45">Net 45</option>
                <option value="60">Net 60</option>
                <option value="90">Net 90</option>
              </select>
              <div className="form-text">
                Invoice due date = Invoice date + payment terms
              </div>
            </div>

            {/* Tax Settings Info */}
            <div className="col-12">
              <div className="alert alert-secondary small">
                <i className="bi bi-info-circle me-2"></i>
                <strong>Note:</strong> These are default rates used to
                pre-populate the tax fields during invoice generation. You can
                adjust the actual tax amounts when creating each invoice.
              </div>
            </div>
          </>
        )}

        {/* Submit Buttons */}
        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !hasChanges()}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              <>
                <i className="bi bi-save"></i> Update Customer
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary ms-2"
            onClick={onCancel}
            disabled={loading}
          >
            <i className="bi bi-x-circle"></i> Cancel
          </button>
          {hasChanges() && !loading && (
            <span className="text-muted ms-3">
              <i className="bi bi-info-circle"></i> You have unsaved changes
            </span>
          )}
        </div>
      </form>

      {/* Audit Trail Notice */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-clock-history"></i> Created:{" "}
          {new Date(customer.created_at).toLocaleString()}
          {customer.updated_at && (
            <>
              <br />
              <i className="bi bi-pencil"></i> Last Updated:{" "}
              {new Date(customer.updated_at).toLocaleString()}
            </>
          )}
        </small>
      </div>
    </div>
  );
};

export default EditCustomerForm;